import axios from 'axios';
import { useState, useContext, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { AuthContext } from '../../context/AuthContext';
import CountryCodeSelect from '../CountryCodeSelect';
import CountryDropdown from '../CountryDropdown';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function SupplierFormModal({
  show,
  onHide,
  onSubmit,
  editingId,
  formData,
  setFormData,
  currencies,
  onCurrencyAdded,
}) {
  const { userData } = useContext(AuthContext);
  const [showAddCurrencyModal, setShowAddCurrencyModal] = useState(false);
  const [selectedNewCurrency, setSelectedNewCurrency] = useState(null);
  const [addingCurrency, setAddingCurrency] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([
    { value: "LKR", label: "LKR (₨) - Sri Lankan Rupee" },
  ]);

  // Fetch currency options from backend API
  const fetchCurrencyOptions = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/currencies/get-all-currency-symbols`
      );
      if (response.data && response.data.currencies) {
        setCurrencyOptions(response.data.currencies);
      }
    } catch (err) {
      setCurrencyOptions([
        { value: "LKR", label: "LKR (₨) - Sri Lankan Rupee" },
        { value: "GBP", label: "GBP (£) - British Pound Sterling" },
        { value: "USD", label: "USD ($) - US Dollar" },
        { value: "EUR", label: "EUR (€) - Euro" },
        { value: "AED", label: "AED (د.إ) - UAE Dirham" },
      ]);
    }
  };

  useEffect(() => {
    fetchCurrencyOptions();
  }, []);

  return (
    <>
      <Modal show={show} onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? "Edit Supplier" : "Add Supplier"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Supplier Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.supplier_name}
                onChange={(e) =>
                  setFormData({ ...formData, supplier_name: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Currency</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  value={formData.currency_id}
                  onChange={(e) =>
                    setFormData({ ...formData, currency_id: e.target.value })
                  }
                  required
                  style={{ flex: 1 }}
                >
                  <option value="">Select currency</option>
                  {currencies.map((cur) => (
                    <option key={cur.currency_id} value={cur.currency_id}>
                      {cur.currency_name} ({cur.short_name})
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="primary"
                  onClick={() => setShowAddCurrencyModal(true)}
                  title="Add New Currency"
                  style={{ padding: "0.375rem 0.75rem", whiteSpace: "nowrap" }}
                >
                  + Add
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <div className="d-flex gap-2">
                <CountryCodeSelect
                  value={formData.countryCode}
                  onChange={(code) =>
                    setFormData((prev) => ({ ...prev, countryCode: code }))
                  }
                  placeholder="Code"
                  isRequired={true}
                  width="100px"
                />
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setFormData({ ...formData, phone: value });
                  }}
                  placeholder="Enter phone number"
                  style={{ flex: 1 }}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Address Line 1 <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.address_line_1}
                onChange={(e) =>
                  setFormData({ ...formData, address_line_1: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label> Address Line 2 </Form.Label>
              <Form.Control
                type="text"
                value={formData.address_line_2}
                onChange={(e) =>
                  setFormData({ ...formData, address_line_2: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>State </Form.Label>
              <Form.Control
                type="text"
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <CountryDropdown
                value={formData.country || ""}
                onChange={(country) =>
                  setFormData({ ...formData, country: country })
                }
                placeholder="Select supplier country"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>BRN (Business Registration Number)</Form.Label>
              <Form.Control
                type="text"
                value={formData.brn}
                onChange={(e) =>
                  setFormData({ ...formData, brn: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>VAT Number</Form.Label>
              <Form.Control
                type="text"
                value={formData.vat}
                onChange={(e) =>
                  setFormData({ ...formData, vat: e.target.value })
                }
              />
            </Form.Group>

            <hr />
            <h6>Bank Details</h6>

            <Form.Group className="mb-3">
              <Form.Label>Bank Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.bank_name}
                onChange={(e) =>
                  setFormData({ ...formData, bank_name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Branch</Form.Label>
              <Form.Control
                type="text"
                value={formData.branch}
                onChange={(e) =>
                  setFormData({ ...formData, branch: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Account Number</Form.Label>
              <Form.Control
                type="text"
                value={formData.account_number}
                onChange={(e) =>
                  setFormData({ ...formData, account_number: e.target.value })
                }
              />
            </Form.Group>

            <hr />
            <h6>Payment Terms</h6>

            <Form.Group className="mb-3">
              <Form.Label>Payment Terms</Form.Label>
              <Form.Select
                value={formData.payment_terms}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_terms: e.target.value,
                    credit_period: "",
                    advance_percentage: "",
                  })
                }
                required
              >
                <option value="Cash">Cash</option>
                <option value="Credit">Credit</option>
                <option value="Advance">Advance</option>
              </Form.Select>
            </Form.Group>

            {formData.payment_terms === "Credit" && (
              <Form.Group className="mb-3">
                <Form.Label>Credit Period (Days)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.credit_period}
                  onChange={(e) =>
                    setFormData({ ...formData, credit_period: e.target.value })
                  }
                  required
                  min="1"
                />
              </Form.Group>
            )}

            {formData.payment_terms === "Advance" && (
              <Form.Group className="mb-3">
                <Form.Label>Advance Percentage (%)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.advance_percentage}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      advance_percentage: e.target.value,
                    })
                  }
                  required
                  min="1"
                  max="100"
                />
              </Form.Group>
            )}

            <Button variant="primary" type="submit">
              {editingId ? "Update" : "Save"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Add Currency Modal */}
      <Modal
        show={showAddCurrencyModal}
        onHide={() => setShowAddCurrencyModal(false)}
        centered
      >
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold">Add New Currency</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!selectedNewCurrency) {
                alert("Please select a currency");
                return;
              }

              setAddingCurrency(true);
              try {
                const response = await axios.post(
                  `${BASE_URL}/api/admin/currencies/add-currencies`,
                  {
                    currency_name: selectedNewCurrency.label,
                    short_name: selectedNewCurrency.value,
                    company_code: userData?.company_code,
                    created_by: userData?.id,
                  },
                );

                if (response.data.success) {
                  // Auto-select the newly added currency
                  if (response.data.currency_id) {
                    setFormData({
                      ...formData,
                      currency_id: response.data.currency_id,
                    });
                  }
                  // Reset form
                  setSelectedNewCurrency(null);
                  setShowAddCurrencyModal(false);
                  // Notify parent to refresh currencies
                  if (onCurrencyAdded) {
                    onCurrencyAdded();
                  }
                }
              } catch (error) {
                alert(error.response?.data?.message || "Error adding currency");
              } finally {
                setAddingCurrency(false);
              }
            }}
          >
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold mb-3">
                Select Currency to Add
              </Form.Label>

              <Select
                options={currencyOptions}
                value={selectedNewCurrency}
                onChange={setSelectedNewCurrency}
                placeholder="Search and select a currency..."
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Form.Group>
            <div className="d-flex gap-3">
              <Button
                variant="primary"
                type="submit"
                disabled={addingCurrency}
                className="flex-grow-1"
                size="lg"
              >
                {addingCurrency ? (
                  <>
                    <span className="me-2">Adding...</span>
                  </>
                ) : (
                  "+ Add Currency"
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setShowAddCurrencyModal(false)}
                className="flex-grow-1"
                size="lg"
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}
